import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../components/Button";
import { MetaData } from "../../../helpers/MetaData";
import JsonLd from "../../../helpers/JsonLD";
import { useWindowResize } from "../../../hooks/useWindowResize";
import { handlerAddPacketInBasket, handlerDeletePacketFromBasket } from "../../../redux/Reducers/packetSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { IPacket } from "../../../types";
import { charlottePhotographyServiceAreas } from "../../../utils/seo";
import newbornLifestyleMobile from "../../Resources/assets/images/newborn-lifestyle-family-at-home-portrait.webp";
import Styles from "./newbornPricing.module.scss";
import PricingNavigation, { getPricingBreadcrumbSchema, PricingCategoryNavigation } from "./PricingNavigation";
import PricingGuideCenter from "./PricingGuideCenter";
import { socialPreviews } from "../../../utils/socialPreviews";

type NewbornPackage = {
  cartImage: string;
  category: string;
  description: string;
  id: string;
  images: string;
  imageAlt: string;
  includes: string[];
  name: string;
  note: string;
  photo?: { desktop: string; mobile: string };
  photoOrientation?: "landscape" | "portrait";
  price: string;
  receives: string[];
  style: string;
  time: string;
};

const newbornPackages: NewbornPackage[] = [
  {
    cartImage:
      "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/desktop/family_description_desktop.webp",
    category: "Natural in-home photography",
    description: "A relaxed newborn session photographed in the comfort of your home.",
    id: "6286116ce4b7bbf978784c93",
    images: "25",
    imageAlt: "Parents holding their newborn baby during a natural lifestyle session at home",
    name: "Lifestyle — Natural & Unposed",
    price: "$350",
    style: "Natural and unposed",
    time: "1–2 hr",
    photo: {
      desktop:
        "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/desktop/family_description_desktop.webp",
      mobile: newbornLifestyleMobile,
    },
    photoOrientation: "portrait",
    note: "This is not a traditional posed newborn session. Studio lighting, backdrops, posing props, and styled newborn setups are not included.",
    includes: [
      "1–2 hour in-home session",
      "Photographed with natural light in your bedroom, nursery, or living space",
      "Relaxed photographs of the baby with parents, siblings, and grandparents",
      "Photographer guidance throughout the session",
      "Simple wraps, hats, and headbands provided",
      "Baby may also wear comfortable outfits provided by the family",
      "Up to 6 people total included, including the baby",
      "Each additional person: $30",
      "Your home must have sufficient natural light; lighting and room selection will be discussed before booking",
    ],
    receives: [
      "Baby, parent, sibling, and family portraits",
      "10 fully retouched high-resolution images",
      "15 additional high-resolution images with color and light correction",
      "25 digital images total",
      "Digital download",
    ],
  },
  {
    cartImage: "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/mobile/cocon_description_mobile.webp",
    category: "Professionally posed newborn photography",
    description:
      "A professionally styled newborn session in which the baby remains wrapped throughout the entire session.",
    id: "62861424e4b7bbf978784c99",
    images: "25",
    imageAlt: "Wrapped newborn baby posed during a professional studio-style session",
    name: "Newborn Wrapped — Posed, Fully Wrapped",
    price: "$400",
    style: "Posed; baby fully wrapped",
    time: "1–2 hr",
    photo: {
      desktop:
        "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/desktop/cocon_description_desktop.webp",
      mobile: "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/mobile/cocon_description_mobile.webp",
    },
    photoOrientation: "landscape",
    note: "Different wraps, colors, textures, backgrounds, props, and accessories may be used. However, the baby is photographed only while wrapped. Baby outfits, dresses, and nude or unwrapped posing are not included.",
    includes: [
      "1–2 hour in-home session",
      "Professional newborn posing and studio lighting",
      "Access to wraps, backgrounds, props, and accessories",
      "Choose 3 wrapped baby setups; or 2 wrapped baby setups plus 1 family setup",
      "Parent and sibling portraits are included when the family setup is selected",
      "The baby remains wrapped in all individual and family photographs",
      "Up to 6 people total included, including the baby",
      "Each additional person: $30",
    ],
    receives: [
      "Wrapped baby portraits and macro detail shots",
      "Family portraits, if the family setup is selected",
      "10 fully retouched high-resolution images",
      "15 additional high-resolution images with color and light correction",
      "25 digital images total",
      "Digital download",
    ],
  },
  {
    cartImage: "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/mobile/light_description_mobile.webp",
    category: "Professionally posed newborn photography",
    description: "A styled newborn session with a choice of wraps, outfits, props, and accessories.",
    id: "628615e2e4b7bbf978784cab",
    images: "30",
    imageAlt: "Newborn baby posed with wraps and props during a styled session",
    name: "Newborn Lite — Posed Session",
    price: "$500",
    style: "Posed; full styling options",
    time: "2–3 hr",
    photo: {
      desktop:
        "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/desktop/classic_description_desktop.webp",
      mobile: "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/mobile/light_description_mobile.webp",
    },
    photoOrientation: "landscape",
    note: "Unlike the Newborn Wrapped Package, this session may include wrapped, dressed, and unwrapped newborn posing.",
    includes: [
      "2–3 hour in-home session",
      "Professional newborn posing and studio lighting",
      "Access to baby dresses, wraps, props, and accessories",
      "Choose 3 baby setups; or 2 baby setups plus 1 family setup",
      "Parent and sibling portraits are included when the family setup is selected",
      "Up to 6 people total included, including the baby",
      "Each additional person: $30",
    ],
    receives: [
      "Baby portraits, family portraits, and macro detail shots",
      "10 fully retouched high-resolution images",
      "20 additional high-resolution images with color and light correction",
      "30 digital images total",
      "Digital download",
    ],
  },
  {
    cartImage:
      "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/mobile/standard_description_mobile.webp",
    category: "Professionally posed newborn photography",
    description: "A full posed newborn session offering additional time, variety, and styling options.",
    id: "628611eee4b7bbf978784c95",
    images: "40",
    imageAlt: "Newborn baby photographed during a full posed studio-style session",
    name: "Newborn Standard — Posed Session",
    price: "$650",
    style: "Posed; more variety",
    time: "2–4 hr",
    photo: {
      desktop:
        "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/desktop/standard_description_desktop.webp",
      mobile:
        "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/mobile/standard_description_mobile.webp",
    },
    photoOrientation: "landscape",
    note: "This package offers more variety than Newborn Lite, with an additional setup and more finished images.",
    includes: [
      "2–4 hour in-home session",
      "Professional newborn posing and studio lighting",
      "Access to baby dresses, wraps, props, and accessories",
      "Choose 4 baby setups; or 3 baby setups plus 1 family setup",
      "Parent and sibling portraits are included when the family setup is selected",
      "Up to 6 people total included, including the baby",
      "Each additional person: $30",
    ],
    receives: [
      "Baby portraits, family portraits, and macro detail shots",
      "15 fully retouched high-resolution images",
      "25 additional high-resolution images with color and light correction",
      "40 digital images total",
      "Digital download",
    ],
  },
  {
    cartImage:
      "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/mobile/premium_description_mobile.webp",
    category: "Complete newborn experience",
    description:
      "The most complete newborn package, offering the greatest variety of setups, styling, and finished images.",
    id: "628616d3e4b7bbf978784cad",
    images: "50 + album",
    imageAlt: "Newborn baby photographed during a premium posed portrait session",
    name: "Newborn Premium — Full Posed Experience",
    price: "$1,000",
    style: "Complete posed experience",
    time: "3–5 hr",
    photo: {
      desktop:
        "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/desktop/premium_description_desktop.webp",
      mobile:
        "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/mobile/premium_description_mobile.webp",
    },
    photoOrientation: "landscape",
    note: "This package includes the largest image collection and a professionally designed photo album.",
    includes: [
      "3–5 hour in-home session",
      "Professional newborn posing and studio lighting",
      "Access to baby dresses, wraps, props, and accessories",
      "Choose 5 baby setups; or 4 baby setups plus 1 family setup",
      "Parent and sibling portraits are included when the family setup is selected",
      "Up to 6 people total included, including the baby",
      "Each additional person: $30",
      "Additional baby setup: $100 each",
    ],
    receives: [
      "Baby portraits, family portraits, and macro detail shots",
      "20 fully retouched high-resolution images",
      "30 additional high-resolution images with color and light correction",
      "50 digital images total",
      "8 × 8-inch, 16-page photo album",
      "Digital download",
    ],
  },
];

const privatePrices = [
  ["Lifestyle", "$550"],
  ["Newborn Wrapped", "$600"],
  ["Newborn Lite", "$700"],
  ["Newborn Standard", "$850"],
  ["Newborn Premium", "$1,200"],
];

const NewbornPricingPage = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [removedPackageId, setRemovedPackageId] = useState<string | null>(null);
  const { packetInBasket } = useAppSelector((state) => state.packets);

  useEffect(() => {
    if (!hash) return;

    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const packageElement = document.getElementById(hash.slice(1));
        packageElement?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [hash]);

  useEffect(() => {
    if (!removedPackageId) return;
    const timeoutId = window.setTimeout(() => setRemovedPackageId(null), 3000);
    return () => window.clearTimeout(timeoutId);
  }, [removedPackageId]);
  const { width } = useWindowResize();
  const canonicalUrl = "https://alenalobacheva.com/prices/newborn/";
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${canonicalUrl}#service`,
    name: "In-Home Newborn Photography in Charlotte, NC",
    description:
      "Lifestyle and professionally posed in-home newborn photography packages in Charlotte, North Carolina.",
    provider: {
      "@type": "LocalBusiness",
      "@id": "https://alenalobacheva.com/#localbusiness",
      name: "Alena Lobacheva Photography",
      telephone: "+1-516-468-4837",
    },
    areaServed: charlottePhotographyServiceAreas,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Newborn Photography Packages",
      itemListElement: newbornPackages.map((item) => ({
        "@type": "Offer",
        name: item.name,
        price: item.price.replace(/[$,]/g, ""),
        priceCurrency: "USD",
        url: canonicalUrl,
      })),
    },
  };

  const buildCartPacket = (item: NewbornPackage): IPacket => ({
    _id: item.id,
    countLocations: "",
    createdAt: "",
    description: item.description,
    descriptionEN: item.includes.join("\n"),
    duration: item.time,
    getFromPhotosession: item.receives.join("\n"),
    getFromPhotosessionEN: item.receives.join("\n"),
    image: item.cartImage,
    imageDescription: item.photo?.desktop || item.cartImage,
    imageDescriptionMobile: item.photo?.mobile || item.cartImage,
    namePacket: item.name.split(" — ")[0],
    photosessionType: "newborn",
    pinned: false,
    price: item.price.replace(/[$,]/g, ""),
    shortDescription: `${item.time === "1 hr" ? "1 hour" : item.time.replace(/\bhr\b/, "hours")} · ${
      item.images
    } digital images`,
  });

  const handlePackageAction = (item: NewbornPackage): void => {
    const isInBasket = packetInBasket.some((packet) => packet._id === item.id);
    if (isInBasket) {
      navigate("/basket");
      return;
    }
    dispatch(handlerAddPacketInBasket(buildCartPacket(item)));
    setRemovedPackageId(null);
  };

  const handleRemovePackage = (item: NewbornPackage): void => {
    dispatch(handlerDeletePacketFromBasket(item.id));
    setRemovedPackageId(item.id);
  };

  return (
    <>
      <MetaData
        title="Newborn Photography Packages & Pricing in Charlotte, NC"
        description="Compare lifestyle and posed newborn photography packages in Charlotte, NC, including session time, setups, finished images, pricing, and booking policies."
        canonicalLink={canonicalUrl}
        {...socialPreviews.pricing.newborn}
        imageAltOG="In-home newborn photography in Charlotte, NC"
        titleOG="Newborn Photography Packages & Pricing in Charlotte, NC"
        descriptionOG="Compare lifestyle and professionally posed newborn photography packages from Alena Lobacheva Photography."
      />
      <JsonLd data={serviceSchema} />
      <JsonLd data={getPricingBreadcrumbSchema("newborn", canonicalUrl)} />
      <main className={Styles.newbornPricing}>
        <PricingNavigation current="newborn" />
        <header className={Styles.newbornPricing__hero}>
          <p className={Styles.newbornPricing__eyebrow}>Newborn photography · Charlotte, NC</p>
          <h1>Newborn Session Pricing &amp; Packages</h1>
          <p className={Styles.newbornPricing__lead}>
            Clear package options for natural lifestyle and professionally posed newborn photography.
          </p>
          <p className={Styles.newbornPricing__styleNote}>
            <strong>Choose the session style first.</strong> Lifestyle and posed newborn sessions are two different
            experiences. Please choose the photography style you want before comparing prices.
          </p>
        </header>

        <section aria-labelledby="newborn-comparison-title">
          <h2 className={Styles.newbornPricing__sectionTitle} id="newborn-comparison-title">
            Quick package comparison
          </h2>
          <div className={Styles.newbornPricing__comparisonWrapper}>
            <table className={Styles.newbornPricing__comparison}>
              <thead>
                <tr>
                  <th>Package</th>
                  <th>Session style</th>
                  <th>Time</th>
                  <th>Images</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {newbornPackages.map((item) => (
                  <tr key={item.name}>
                    <td>{item.name.split(" — ")[0]}</td>
                    <td>{item.style}</td>
                    <td>{item.time}</td>
                    <td>{item.images}</td>
                    <td>{item.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={Styles.newbornPricing__setupNote}>
            <strong>What is a setup?</strong> One setup is one coordinated background, prop, color palette, or styled
            scene. Family portraits count as one setup.
          </p>
        </section>

        <section aria-labelledby="newborn-packages-title">
          <h2 className={Styles.newbornPricing__sectionTitle} id="newborn-packages-title">
            Explore every package
          </h2>
          <div className={Styles.newbornPricing__packages}>
            {newbornPackages.map((item) => (
              <details
                className={Styles.package}
                key={item.name}
                id={item.id}
                open={width > 760 || hash === `#${item.id}`}
              >
                <summary className={Styles.package__summary}>
                  <span className={Styles.package__heading}>
                    <span className={Styles.package__name}>{item.name}</span>
                    <span className={Styles.package__price}>{item.price}</span>
                  </span>
                </summary>
                <div className={`${Styles.package__body} ${item.photo ? Styles.package__body_withImage : ""}`}>
                  {item.photo && <p className={Styles.package__description}>{item.description}</p>}
                  {item.photo && (
                    <picture
                      className={`${Styles.package__picture} ${
                        Styles[`package__picture_${item.photoOrientation || "landscape"}`]
                      } ${Styles.package__picture_mobileLandscape}`}
                    >
                      <source media="(max-width: 760px)" srcSet={item.photo.mobile} />
                      <img alt={item.imageAlt} decoding="async" loading="lazy" src={item.photo.desktop} />
                    </picture>
                  )}
                  {!item.photo && (
                    <div className={Styles.package__introduction}>
                      <p>{item.description}</p>
                      <p className={Styles.package__note}>
                        <strong>Please note.</strong> {item.note}
                      </p>
                    </div>
                  )}
                  <div className={Styles.package__lists}>
                    <div>
                      <h3>Session includes</h3>
                      <ul>
                        {item.includes.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3>What you receive</h3>
                      <ul>
                        {item.receives.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                      {item.photo && (
                        <p className={Styles.package__note}>
                          <strong>Please note.</strong> {item.note}
                        </p>
                      )}
                      <div className={Styles.package__orderButton}>
                        <Button
                          styleButton="ping"
                          edit
                          type="button"
                          editStyle={
                            packetInBasket.some((packet) => packet._id === item.id) ? "green" : "buttonPriceDescription"
                          }
                          onClick={() => handlePackageAction(item)}
                        >
                          {packetInBasket.some((packet) => packet._id === item.id)
                            ? "Go to cart"
                            : "Add package to cart"}
                        </Button>
                        {packetInBasket.some((packet) => packet._id === item.id) && (
                          <button
                            className={Styles.package__removeButton}
                            type="button"
                            onClick={() => handleRemovePackage(item)}
                          >
                            Remove from cart
                          </button>
                        )}
                      </div>
                      {removedPackageId === item.id && (
                        <p className={Styles.package__cartNotice} role="status">
                          Package removed from your cart.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className={Styles.newbornPricing__policies} aria-label="Booking and privacy information">
          <article className={Styles.policy}>
            <p className={Styles.newbornPricing__eyebrow}>Booking policies</p>
            <h2>Important Session Information</h2>
            <h3>Baby&apos;s comfort and safety</h3>
            <p>
              The number and variety of completed poses depend on the baby&apos;s comfort, feeding needs, sleep, and
              safety. Specific poses cannot be guaranteed. The session always follows the baby&apos;s pace.
            </p>
            <h3>Retainer</h3>
            <p>
              A $100 non-refundable retainer is required to reserve your session date and time. The retainer is applied
              toward the total package price. One rescheduling is permitted with at least 48 hours&apos; notice.
            </p>
            <h3>Travel fee</h3>
            <p>
              Travel within the Charlotte area starts at $30 and is calculated based on the session address. Additional
              charges may apply for locations outside Charlotte, tolls, or paid parking. The exact travel fee will be
              confirmed before booking.
            </p>
            <h3>Additional images</h3>
            <ul>
              <li>Additional fully retouched images: $25 each</li>
              <li>All remaining usable unretouched JPEG images from the session: $100</li>
              <li>RAW files are not provided</li>
            </ul>
          </article>
          <article className={`${Styles.policy} ${Styles.policy_private}`}>
            <p className={Styles.newbornPricing__eyebrow}>Privacy options</p>
            <h2>Portfolio-Use Discount &amp; Private Sessions</h2>
            <p className={Styles.policy__pricingNote}>
              <strong>Pricing note.</strong> The package prices shown include a $200 portfolio-use discount and require
              a signed Model Release allowing selected images to be used in Alena Lobacheva Photography&apos;s
              portfolio, website, and social media.
            </p>
            <p>
              Clients may choose to keep their session completely private. In that case, no images will be shared
              publicly, and the regular session price will apply:
            </p>
            <dl className={Styles.policy__privatePrices}>
              {privatePrices.map(([name, price]) => (
                <div key={name}>
                  <dt>{name}</dt>
                  <dd>{price}</dd>
                </div>
              ))}
            </dl>
          </article>
        </section>
        <PricingGuideCenter category="newborn" />
        <section className={Styles.newbornPricing__cta}>
          <h2>Ready to plan your newborn session?</h2>
          <p>Choose the experience that feels right for your family and reserve your preferred date.</p>
          <Link className={Styles.newbornPricing__ctaLink} to="/contact/">
            Contact Alena
          </Link>
        </section>
        <PricingCategoryNavigation current="newborn" />
      </main>
    </>
  );
};

export default NewbornPricingPage;
